import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "#/components/shared/LegalPage";
import { BUSINESS } from "#/lib/constants";

const TITLE = "Terms of Service | Blue Rock Concrete";
const DESCRIPTION =
  "The terms that govern your use of the Blue Rock Concrete website, our estimates, and the information published here.";
const URL = "https://bluerockconcreteco.com/terms";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <LegalPage title="Terms of Service" updated="July 26, 2026">
      <p>
        These Terms of Service ("Terms") govern your use of{" "}
        <strong>bluerockconcreteco.com</strong> (the "Website"), operated by{" "}
        <strong>{BUSINESS.name}</strong> ("Company," "we," "us," or "our"). Read
        them alongside our <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>1. Acceptance of Terms</h2>
      <p>
        By visiting or using the Website you agree to these Terms. If you do not
        agree with them, please do not use the Website. If you use the Website on
        behalf of a company, you confirm you are authorized to accept these Terms
        for that company.
      </p>

      <h2>2. What This Website Is</h2>
      <p>
        The Website presents information about our concrete services in Delaware,
        Pennsylvania, and Maryland, shows past work, and lets you request a free
        estimate or send an inquiry. It is informational and does not itself sell
        anything or take payment.
      </p>

      <h2>3. Estimates and Quotes</h2>
      <p>
        Submitting the estimate form starts a conversation. It does not create a
        contract, reserve a place on our schedule, or obligate either of us to
        anything.
      </p>
      <ul>
        <li>
          Estimates are free and carry no obligation to hire us.
        </li>
        <li>
          Any figure given before we visit the property is a rough guide only.
          Accurate pricing requires an on-site measurement.
        </li>
        <li>
          Work begins only under a separate written agreement signed by both
          parties. That agreement, not this Website, defines the scope, price,
          schedule, and warranty for your project.
        </li>
        <li>
          Prices quoted are valid for the period stated in the written estimate
          and may change with material costs, site conditions found after
          excavation, or a change in scope you request.
        </li>
        <li>
          Schedules for concrete work depend on weather. Pouring and finishing
          require suitable temperature and moisture, and we may reschedule to
          protect the quality of the work.
        </li>
      </ul>

      <h2>4. What You Send Us</h2>
      <p>
        You are responsible for the accuracy of what you submit. Do not submit
        anything false, or anyone else's personal information without their
        permission. You confirm you own or are authorized to request work at any
        property address you give us. We handle submissions as described in our{" "}
        <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>5. Intellectual Property</h2>
      <p>
        The Website and its contents, including text, photographs of our
        projects, layout, graphics, and logos, belong to {BUSINESS.name} or are
        used with permission, and are protected by copyright and trademark law.
      </p>
      <p>
        You may view and print pages for your own personal, non-commercial use.
        You may not copy, republish, sell, or use our project photography or
        copy for any other purpose without our written permission.
      </p>

      <h2>6. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Website for any unlawful purpose</li>
        <li>
          Submit false requests, spam, or automated form submissions
        </li>
        <li>
          Attempt to gain unauthorized access to the Website, its server, or any
          connected system
        </li>
        <li>
          Interfere with the Website's operation, including by introducing
          malicious code or overloading it
        </li>
        <li>
          Scrape, harvest, or copy content in bulk without our permission
        </li>
        <li>Impersonate anyone or misrepresent your affiliation</li>
      </ul>

      <h2>7. Third-Party Links</h2>
      <p>
        The Website links to third-party sites such as our social profiles and
        Google Business Profile. We do not control them and are not responsible
        for their content, accuracy, or practices. Following a link is at your
        own risk.
      </p>

      <h2>8. Disclaimer</h2>
      <p>
        The Website is provided "as is" and "as available," without warranties of
        any kind, express or implied, to the fullest extent the law allows. We do
        not warrant that the Website will be uninterrupted, error free, or that
        the information on it is complete or current.
      </p>
      <p>
        Project photographs show work we have completed. Concrete is a natural
        material and finish, color, and texture vary between pours. Photographs
        are representative, not a guarantee that your project will look
        identical. Concrete is also expected to develop hairline cracking over
        time as it cures and moves with the ground and the seasons. Nothing on
        this Website is a warranty. The written agreement for your project is the
        only place warranties are given.
      </p>
      <p>
        Information here is general and is not engineering, legal, or
        professional advice for your specific property.
      </p>

      <h2>9. Limitation of Liability</h2>
      <p>
        To the fullest extent permitted by law, {BUSINESS.name} is not liable for
        any indirect, incidental, special, consequential, or punitive damages, or
        any loss of profits or data, arising from your use of the Website. This
        section limits liability arising from the Website only. It does not limit
        our obligations under a signed agreement for work performed, and it does
        not exclude any liability that cannot lawfully be excluded.
      </p>

      <h2>10. Indemnification</h2>
      <p>
        You agree to indemnify and hold harmless {BUSINESS.name} and its owners,
        employees, and subcontractors from any claim or demand, including
        reasonable legal fees, arising from your misuse of the Website or your
        breach of these Terms.
      </p>

      <h2>11. Governing Law</h2>
      <p>
        These Terms are governed by the laws of the State of Delaware, without
        regard to its conflict of law rules. Any dispute arising from the Website
        or these Terms is subject to the exclusive jurisdiction of the state and
        federal courts located in Delaware.
      </p>

      <h2>12. Changes to These Terms</h2>
      <p>
        We may update these Terms at any time. The revised version takes effect
        when posted, and the date at the top of this page shows when it last
        changed. Continuing to use the Website after a change means you accept
        it.
      </p>

      <h2>13. Severability and Entire Agreement</h2>
      <p>
        If any provision of these Terms is found unenforceable, the rest stay in
        force. These Terms and the Privacy Policy are the entire agreement
        between you and us regarding the Website, and they replace any earlier
        understanding about it. They do not replace a signed agreement for
        construction work.
      </p>

      <h2>14. Contact Us</h2>
      <p>Questions about these Terms:</p>
      <ul>
        <li>
          Email: <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>
        </li>
        <li>
          Phone: <a href={BUSINESS.phoneHref}>{BUSINESS.phone}</a>
        </li>
        <li>{BUSINESS.name}, {BUSINESS.address}</li>
      </ul>
    </LegalPage>
  );
}
